import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdWorkOutline,
  MdMailOutline,
  MdPhone,
  MdSearch,
  MdViewModule,
  MdViewList,
  MdMoreVert,
  MdChevronLeft,
  MdChevronRight,
  MdAccessTime,
} from 'react-icons/md';
import employeeAttendanceHrAPI from '../../api/employeeAttendanceHrAPI';
import { useEmployeeAttendanceAuth } from '../../context/EmployeeAttendanceAuthContext';
import { useEmployeeAttendanceHrAuth } from '../../context/EmployeeAttendanceHrAuthContext';
import { getInitials } from '../../utils/attendanceFormatters';
import { fetchWithHrToken, HR_SESSION_ERROR } from '../../utils/hrApiHelpers';
import HrPortalAccessModal from './HrPortalAccessModal';
import '../../employeeAttendance.css';
import '../../employeeAttendance-hr.css';

const PAGE_SIZE = 80;
const ACTIVE_FILTERS = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Archived' },
  { value: 'all', label: 'All' },
];

function resolveImageUrl(emp, baseURL) {
  const raw = emp.image_url || emp.image_128 || emp.avatar_url;
  if (!raw || raw === 'false') return null;
  if (String(raw).startsWith('data:') || String(raw).startsWith('http')) return raw;
  const base = (baseURL || '').replace(/\/$/, '');
  return `${base}${raw.startsWith('/') ? raw : `/${raw}`}`;
}

function EmployeeAvatar({ emp, baseURL }) {
  const [broken, setBroken] = useState(false);
  const src = resolveImageUrl(emp, baseURL);
  if (src && !broken) {
    return (
      <img
        className="ea-odoo-avatar__img"
        src={src}
        alt=""
        onError={() => setBroken(true)}
      />
    );
  }
  return <span className="ea-odoo-avatar__initials">{getInitials(emp.name)}</span>;
}

function CardMenu({ emp, busy, onEdit, onPortal, onArchive, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);

  return (
    <div className="ea-odoo-menu" ref={ref} role="menu">
      <button type="button" role="menuitem" onClick={onEdit} disabled={busy}>
        Edit
      </button>
      {emp.active !== false && (
        <button type="button" role="menuitem" onClick={onPortal} disabled={busy}>
          {emp.has_portal_access ? 'Portal access' : 'Grant portal'}
        </button>
      )}
      <button
        type="button"
        role="menuitem"
        className={emp.active === false ? '' : 'ea-odoo-menu__danger'}
        onClick={onArchive}
        disabled={busy}
      >
        {emp.active === false ? 'Restore' : 'Archive'}
      </button>
    </div>
  );
}

export default function HrEmployeesPage() {
  const navigate = useNavigate();
  const { ensureHrToken } = useEmployeeAttendanceHrAuth();
  const { user: staffUser } = useEmployeeAttendanceAuth();

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [viewMode, setViewMode] = useState('kanban');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [activeFilter, setActiveFilter] = useState('true');
  const [portalOnly, setPortalOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [portalResult, setPortalResult] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  const openEmployee = (id) => {
    navigate(`/bu-dashboard/hr/employees/${id}`);
  };

  const openNew = () => {
    navigate('/bu-dashboard/hr/employees/new');
  };

  const loadDepartments = useCallback(async () => {
    const { data } = await fetchWithHrToken(ensureHrToken, (token) =>
      employeeAttendanceHrAPI.getDepartments(token)
    );
    if (data?.departments) setDepartments(data.departments);
  }, [ensureHrToken]);

  const loadEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        search: search || undefined,
        department_id: departmentId ? Number(departmentId) : undefined,
        active: activeFilter === 'all' ? 'all' : activeFilter === 'true',
        portal_only: portalOnly,
        limit: PAGE_SIZE,
        offset,
      };
      const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
        employeeAttendanceHrAPI.getEmployees(params, token)
      );
      if (fetchError === 'no_token') {
        setError(staffUser?.hr_api_error || HR_SESSION_ERROR);
        setEmployees([]);
        setTotal(0);
        return;
      }
      if (fetchError) {
        setError(fetchError);
        setEmployees([]);
        setTotal(0);
        return;
      }
      setEmployees(data.employees || []);
      setTotal(data.total ?? (data.employees || []).length);
    } catch (err) {
      setError(err.message || 'Failed to load employees');
      setEmployees([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    ensureHrToken,
    search,
    departmentId,
    activeFilter,
    portalOnly,
    offset,
    staffUser?.hr_api_error,
  ]);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    const t = setTimeout(() => {
      setOffset(0);
      setSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const grantPortal = async (employee) => {
    setOpenMenuId(null);
    setBusyId(employee.id);
    setActionError(null);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      employeeAttendanceHrAPI.createPortalAccess(
        { employee_id: employee.id, auto_generate_password: true },
        token
      )
    );
    setBusyId(null);
    if (fetchError === 'no_token') {
      setActionError(HR_SESSION_ERROR);
      return;
    }
    if (fetchError) {
      setActionError(fetchError);
      return;
    }
    setPortalResult(data);
    loadEmployees();
  };

  const toggleArchive = async (employee) => {
    setOpenMenuId(null);
    const nextActive = !employee.active;
    const label = nextActive ? 'restore' : 'archive';
    if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} ${employee.name}?`)) {
      return;
    }
    setBusyId(employee.id);
    setActionError(null);
    const { error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      employeeAttendanceHrAPI.archiveEmployee(employee.id, nextActive, token)
    );
    setBusyId(null);
    if (fetchError === 'no_token') {
      setActionError(HR_SESSION_ERROR);
      return;
    }
    if (fetchError) {
      setActionError(fetchError);
      return;
    }
    loadEmployees();
  };

  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  const apiBase = employeeAttendanceHrAPI.baseURL;

  return (
    <div className="ea-hr-dash ea-odoo-emp">
      <div className="ea-odoo-control">
        <div className="ea-odoo-control__left">
          <button type="button" className="ea-odoo-new" onClick={openNew}>
            New
          </button>
          <div className="ea-odoo-control__title">
            <span>Employees</span>
          </div>
        </div>

        <div className="ea-odoo-search">
          <MdSearch className="ea-odoo-search__icon" aria-hidden />
          <input
            type="search"
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search employees"
          />
        </div>

        <div className="ea-odoo-control__right">
          <div className="ea-odoo-pager" aria-label="Pagination">
            <span>
              {loading ? '…' : `${rangeStart}-${rangeEnd} / ${total}`}
            </span>
            <button
              type="button"
              className="ea-odoo-pager__btn"
              disabled={!canPrev || loading}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              aria-label="Previous page"
            >
              <MdChevronLeft size={20} />
            </button>
            <button
              type="button"
              className="ea-odoo-pager__btn"
              disabled={!canNext || loading}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              aria-label="Next page"
            >
              <MdChevronRight size={20} />
            </button>
          </div>
          <div className="ea-odoo-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={viewMode === 'kanban' ? 'active' : ''}
              onClick={() => setViewMode('kanban')}
              aria-label="Kanban view"
              title="Kanban"
            >
              <MdViewModule size={20} />
            </button>
            <button
              type="button"
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
              aria-label="List view"
              title="List"
            >
              <MdViewList size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="ea-odoo-filters">
        <select
          className="ea-odoo-filters__select"
          value={departmentId}
          onChange={(e) => {
            setOffset(0);
            setDepartmentId(e.target.value);
          }}
          aria-label="Department"
        >
          <option value="">All departments</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        <select
          className="ea-odoo-filters__select"
          value={activeFilter}
          onChange={(e) => {
            setOffset(0);
            setActiveFilter(e.target.value);
          }}
          aria-label="Status"
        >
          {ACTIVE_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <label className="ea-odoo-filters__check">
          <input
            type="checkbox"
            checked={portalOnly}
            onChange={(e) => {
              setOffset(0);
              setPortalOnly(e.target.checked);
            }}
          />
          Portal only
        </label>
      </div>

      {(error || actionError) && (
        <div className="ea-toast error show ea-dash-error" role="alert">
          {error || actionError}
        </div>
      )}

      {loading && employees.length === 0 ? (
        <div className="ea-odoo-empty">Loading employees…</div>
      ) : employees.length === 0 ? (
        <div className="ea-odoo-empty">No employees match your filters.</div>
      ) : viewMode === 'kanban' ? (
        <div className="ea-odoo-kanban">
          {employees.map((emp) => {
            const email = emp.work_email || emp.user_login;
            const phone = emp.work_phone || emp.mobile_phone;
            return (
              <article
                key={emp.id}
                className={`ea-odoo-card${emp.active === false ? ' ea-odoo-card--archived' : ''}`}
              >
                <button
                  type="button"
                  className="ea-odoo-card__main"
                  onClick={() => openEmployee(emp.id)}
                  aria-label={`Open ${emp.name}`}
                >
                  <div className="ea-odoo-avatar">
                    <EmployeeAvatar emp={emp} baseURL={apiBase} />
                  </div>
                  <div className="ea-odoo-card__body">
                    <h3 className="ea-odoo-card__name">{emp.name}</h3>
                    {emp.job_title && (
                      <p className="ea-odoo-card__line">
                        <MdWorkOutline aria-hidden />
                        <span>{emp.job_title}</span>
                      </p>
                    )}
                    {email && (
                      <p className="ea-odoo-card__line">
                        <MdMailOutline aria-hidden />
                        <span>{email}</span>
                      </p>
                    )}
                    {phone && (
                      <p className="ea-odoo-card__line">
                        <MdPhone aria-hidden />
                        <span>{phone}</span>
                      </p>
                    )}
                    {!emp.job_title && !email && !phone && emp.department_name && (
                      <p className="ea-odoo-card__line">
                        <MdWorkOutline aria-hidden />
                        <span>{emp.department_name}</span>
                      </p>
                    )}
                  </div>
                </button>

                <span
                  className={`ea-odoo-card__dot${
                    emp.active === false ? ' ea-odoo-card__dot--off' : ''
                  }`}
                  title={emp.active === false ? 'Archived' : 'Active'}
                />

                <div className="ea-odoo-card__footer">
                  <div className="ea-odoo-card__badges">
                    {emp.has_portal_access ? (
                      <span className="ea-odoo-badge ea-odoo-badge--portal" title="Portal access">
                        P
                      </span>
                    ) : (
                      <span className="ea-odoo-badge ea-odoo-badge--muted" title="No portal">
                        —
                      </span>
                    )}
                    <span className="ea-odoo-badge ea-odoo-badge--clock" title="Attendance">
                      <MdAccessTime size={14} />
                    </span>
                  </div>
                  <button
                    type="button"
                    className="ea-odoo-card__more"
                    aria-label="More actions"
                    aria-expanded={openMenuId === emp.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId((id) => (id === emp.id ? null : emp.id));
                    }}
                  >
                    <MdMoreVert size={18} />
                  </button>
                  {openMenuId === emp.id && (
                    <CardMenu
                      emp={emp}
                      busy={busyId === emp.id}
                      onClose={() => setOpenMenuId(null)}
                      onEdit={() => {
                        setOpenMenuId(null);
                        openEmployee(emp.id);
                      }}
                      onPortal={() => grantPortal(emp)}
                      onArchive={() => toggleArchive(emp)}
                    />
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="ea-odoo-list-wrap">
          <table className="ea-odoo-list">
            <thead>
              <tr>
                <th />
                <th>Name</th>
                <th>Job title</th>
                <th>Work email</th>
                <th>Department</th>
                <th>Portal</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => (
                <tr
                  key={emp.id}
                  className={emp.active === false ? 'ea-odoo-list__row--muted' : ''}
                  onClick={() => openEmployee(emp.id)}
                >
                  <td className="ea-odoo-list__avatar-cell">
                    <div className="ea-odoo-avatar ea-odoo-avatar--sm">
                      <EmployeeAvatar emp={emp} baseURL={apiBase} />
                    </div>
                  </td>
                  <td className="ea-odoo-list__name">{emp.name}</td>
                  <td>{emp.job_title || ''}</td>
                  <td>{emp.work_email || emp.user_login || ''}</td>
                  <td>{emp.department_name || ''}</td>
                  <td>
                    {emp.has_portal_access ? (
                      <span className="ea-odoo-badge ea-odoo-badge--portal">P</span>
                    ) : (
                      ''
                    )}
                  </td>
                  <td
                    className="ea-odoo-list__actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      className="ea-odoo-card__more"
                      aria-label="More actions"
                      onClick={() =>
                        setOpenMenuId((id) => (id === emp.id ? null : emp.id))
                      }
                    >
                      <MdMoreVert size={18} />
                    </button>
                    {openMenuId === emp.id && (
                      <CardMenu
                        emp={emp}
                        busy={busyId === emp.id}
                        onClose={() => setOpenMenuId(null)}
                        onEdit={() => {
                          setOpenMenuId(null);
                          openEmployee(emp.id);
                        }}
                        onPortal={() => grantPortal(emp)}
                        onArchive={() => toggleArchive(emp)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {portalResult && (
        <HrPortalAccessModal result={portalResult} onClose={() => setPortalResult(null)} />
      )}
    </div>
  );
}
